import React, { useLayoutEffect, useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import MobileTabBar from './components/MobileTabBar';
import Footer from './components/Footer';
import Home from './pages/Home';
// 路由页面统一懒加载；链接悬停时会提前下载对应代码块。
import {
  Store, GameDetail, Community, PostDetail, Profile, Library, Cart,
  Creator, Wallet, MapStore, NewMap, MyWorks, MapSettings, CodeMarket,
  ShareView, Admin, MessageReports, Rules, Privacy, Settings, Messages,
  Notifications, LevelBenefits, Activities, Login, Register, LoginRecords,
  Social, Mails, Feedback, Daily, Achievements, CustomerService, TestPage,
  Changelog, InteractiveEditor, Downloads, prefetchByHref, prefetchRoute,
} from './utils/routeChunks';
import ErrorBoundary from './components/ErrorBoundary';
import WelcomeFlow from './components/WelcomeFlow';
import { useAuthStore } from './store/authStore';
import { Toaster, toast } from 'react-hot-toast';
import { LanguageProvider } from './context/LanguageContext';
import axios from 'axios';
import { io } from 'socket.io-client';
import { FiHeart, FiMessageCircle, FiShare2, FiFlag } from 'react-icons/fi';
import { BsEmojiSmile } from 'react-icons/bs';
import { getDefaultAvatar } from './utils/defaultAvatar';
import { LEVEL_BENEFITS, getFrameVisual } from './utils/levelSystem';
import './components/Skeleton.css';
import './components/RouteProgress.css';

const API = '/api';
const DEFAULT_EMOJIS = ['😃', '😱', '😭', '😎', '😆', '🍎', '🤯', '🤪'];

// 路由代码加载时只显示细进度线，页面自身的数据骨架保持不变。
function RouteFallback() {
  return (
    <div className="route-progress" aria-hidden="true">
      <div className="route-progress-bar" />
    </div>
  );
}

// 词语社区组件
function KeywordCommunityInline() {
  const { keyword } = useParams();
  const navigate = useNavigate();
  const { user, getHeaders } = useAuthStore();
  const [posts, setPosts] = useState([]);
  const [postsPage, setPostsPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedEmojis, setSelectedEmojis] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [emojiSeries, setEmojiSeries] = useState([]);
  const [activeSeries, setActiveSeries] = useState('default');
  const helpTimer = React.useRef(null);
  const helpContainerRef = React.useRef(null);

  // 获取表情系列数据
  function fetchEmojiSeries() {
    if (!user) return;
    axios.get(`${API}/community/emoji-series`, { headers: getHeaders() })
      .then(res => {
        const series = res.data.series || [];
        setEmojiSeries(series);
        if (!series.find(s => s.key === activeSeries && s.unlocked)) {
          const first = series.find(s => s.unlocked);
          if (first) setActiveSeries(first.key);
        }
      }).catch(() => {});
  }

  useEffect(() => { if (keyword) fetchPosts(); }, [keyword]);
  useEffect(() => { if (user) fetchEmojiSeries(); }, [user]);

  // 自己的头像框以服务器为准；本地缓存只作为旧数据和断网兜底。
  function getAvatarFrameId() {
    if (user?.avatar_frame) return user.avatar_frame;
    try {
      const saved = localStorage.getItem(`frames_${user?.id}`);
      if (saved) {
        const settings = JSON.parse(saved);
        if (settings.avatarFrame) return settings.avatarFrame;
      }
    } catch(e) {}
    return 'frame_default';
  }
  function getAvatarFrameStyle() {
    try {
      const frameId = user?.avatar_frame || getAvatarFrameId();
      const allFrames = Object.values(LEVEL_BENEFITS.avatarFrames);
      const frameObj = allFrames.find(f => f.id === frameId);
      if (frameObj) return getFrameVisual(frameId);
    } catch(e) {}
    return { background: '#4a6b8a' };
  }

  function handleHelpMouseEnter() {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    helpTimer.current = setTimeout(() => setShowHelp(true), 1000);
  }
  function handleHelpMouseLeave() {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    helpTimer.current = setTimeout(() => setShowHelp(false), 300);
  }
  function handlePopupMouseEnter() {
    if (helpTimer.current) clearTimeout(helpTimer.current);
  }
  function handleClickHelp() {
    if (helpTimer.current) clearTimeout(helpTimer.current);
    setShowHelp(!showHelp);
  }

  function fetchPosts(reset = false) {
    const page = reset ? 1 : postsPage;
    setLoading(reset);
    setLoadingMore(!reset);
    axios.get(`${API}/community/keyword-posts`, { params: { keyword: decodeURIComponent(keyword), page, limit: 20 } })
      .then(res => {
        const data = res.data || { posts: [], hasMore: false };
        setPosts(reset ? data.posts : [...posts, ...data.posts]);
        setHasMorePosts(data.hasMore);
        setPostsPage(page + 1);
      }).catch(() => {
        if (reset) setPosts([]);
      }).finally(() => { setLoading(false); setLoadingMore(false); });
  }

  // 关键词变化时重置
  useEffect(() => { if (keyword) { setPostsPage(1); setHasMorePosts(false); fetchPosts(true); } }, [keyword]);

  function handlePostEmoji() {
    if (selectedEmojis.length === 0 || !user) return;
    const emojiStr = selectedEmojis.join('');
    axios.post(`${API}/community/keyword-posts`, { keyword: decodeURIComponent(keyword), emoji: emojiStr }, { headers: getHeaders() })
      .then(() => { setSelectedEmojis([]); setShowEmojiPicker(false); setPostsPage(1); setHasMorePosts(false); fetchPosts(true); fetchEmojiSeries(); toast.success('发表成功'); }).catch(() => toast.error('发表失败'));
  }
  function addEmoji(e) {
    if (selectedEmojis.length >= 8) {
      toast.error('表情的家填满啦！');
      return;
    }
    setSelectedEmojis([...selectedEmojis, e]);
  }
  function removeEmoji(idx) {
    setSelectedEmojis(selectedEmojis.filter((_, i) => i !== idx));
  }
  function handleLike(pid) { if (!user) return; axios.post(`${API}/community/keyword-posts/${pid}/like}`, {}, { headers: getHeaders() }).then(() => fetchPosts()).catch(() => {}); }

  return (
    <>
    <div style={{ maxWidth:800, margin:'0 auto', padding:20 }}>
      <div style={{ marginBottom:20 }}>
        <button onClick={() => navigate(-1)} style={{ background:'none',border:'none',color:'#4fa3f7',cursor:'pointer',fontSize:14 }}>&larr; 返回</button>
        <h2 style={{ color:'#fff',marginTop:10 }}>&quot;{decodeURIComponent(keyword)}&quot; 的社区</h2>
        <p style={{ color:'#8f98a0',fontSize:13 }}>快来一起发表情吧！</p>
      </div>
      {user && (
        <div style={{ background:'rgba(30,34,50,0.95)',borderRadius:12,padding:16,marginBottom:20,border:'1px solid rgba(255,255,255,0.1)', position:'relative', userSelect:'none', WebkitUserSelect:'none' }}>
          <div style={{ display:'flex',alignItems:'center',gap:10 }}>
            <div style={{ minWidth:40,minHeight:40,padding:'4px 8px',borderRadius:8,background:'rgba(255,255,255,0.05)',display:'flex',flexDirection:'column',alignItems:'flex-start',fontSize:28,border:'1px solid rgba(255,255,255,0.1)',userSelect:'none',WebkitUserSelect:'none',position:'relative' }}>
              <div onClick={() => setShowEmojiPicker(!showEmojiPicker)} style={{ display:'flex',alignItems:'center',gap:2,flexWrap:'wrap',cursor:'pointer',position:'relative' }}>
                {selectedEmojis.length === 0 ? <BsEmojiSmile/> : selectedEmojis.map((e, i) => (
                  <span key={i} onClick={(ev) => { ev.stopPropagation(); removeEmoji(i); }} style={{cursor:'pointer',fontSize:28,userSelect:'none',WebkitUserSelect:'none'}}>{e}</span>
                ))}
              </div>
            </div>
            <div 
              ref={helpContainerRef}
              onMouseEnter={handleHelpMouseEnter}
              onMouseLeave={handleHelpMouseLeave}
              className="help-popup-container" 
              style={{ position:'relative' }}
            >
              <div onClick={handleClickHelp} style={{ width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:'bold',color:'#8f98a0',background:'rgba(255,255,255,0.05)',borderRadius:'50%',cursor:'pointer',border:'1px solid rgba(255,255,255,0.1)' }}>?</div>
              {showHelp && (
                <div 
                  onMouseEnter={handlePopupMouseEnter}
                  onMouseLeave={handleHelpMouseLeave}
                  style={{ position:'absolute',top:36,right:0,background:'#1b2838',border:'1px solid #4a6b8a',borderRadius:8,padding:16,minWidth:240,zIndex:1000,boxShadow:'0 4px 12px rgba(0,0,0,0.5)' }}
                >
                  <div style={{ color:'#c6d4df',fontSize:13,marginBottom:12 }}>
                    <div style={{ fontWeight:'bold',marginBottom:4 }}>如何删除添加的表情？</div>
                    <div style={{ color:'#8f98a0' }}>点击上方你已选择的表情删除</div>
                  </div>
                  <div style={{ color:'#c6d4df',fontSize:13 }}>
                    <div style={{ fontWeight:'bold',marginBottom:4 }}>如何添加表情？</div>
                    <div style={{ color:'#8f98a0' }}>点击下方表情按钮，添加表情</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {showEmojiPicker && (
            <div style={{ marginTop:12,padding:12,background:'rgba(0,0,0,0.3)',borderRadius:8 }}>
              {emojiSeries.length > 0 && (
                <div style={{ display:'flex',gap:6,marginBottom:12,flexWrap:'wrap' }}>
                  {emojiSeries.filter(s => s.unlocked).map(s => (
                    <button key={s.key} onClick={() => setActiveSeries(s.key)} style={{
                      padding:'4px 12px',borderRadius:6,border:'1px solid rgba(255,255,255,0.15)',
                      background: activeSeries === s.key ? 'rgba(79,163,247,0.3)' : 'rgba(255,255,255,0.05)',
                      color:'#c6d4df',fontSize:12,cursor:'pointer',whiteSpace:'nowrap'
                    }}>{s.name}</button>
                  ))}
                </div>
              )}
              <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
                {(emojiSeries.find(s => s.key === activeSeries && s.unlocked)?.emojis || DEFAULT_EMOJIS).map((e,i) => (
                  <div key={i} onClick={()=>addEmoji(e)} style={{ width:40,height:40,display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,cursor:'pointer',borderRadius:8,background:selectedEmojis.length<8?'rgba(255,255,255,0.05)':'rgba(255,255,255,0.02)',opacity:selectedEmojis.length<8?1:0.3,userSelect:'none',WebkitUserSelect:'none' }}>{e}</div>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop:12,display:'flex',justifyContent:'flex-end' }}>
            <button
              onClick={handlePostEmoji}
              disabled={selectedEmojis.length === 0}
              style={{
                padding:'8px 28px',
                borderRadius:8,
                border:'none',
                cursor:selectedEmojis.length > 0 ? 'pointer' : 'not-allowed',
                fontSize:14,
                fontWeight:'bold',
                background:selectedEmojis.length > 0 ? 'linear-gradient(135deg,#4fa3f7,#3b82f6)' : 'rgba(255,255,255,0.05)',
                color:selectedEmojis.length > 0 ? '#fff' : '#555',
                boxShadow:selectedEmojis.length > 0 ? '0 2px 10px rgba(79,163,247,0.3)' : 'none',
                transition:'all 0.2s'
              }}
            >
              发布
            </button>
          </div>
        </div>
      )}
      {loading ? <div style={{ textAlign:'center',color:'#8f98a0',padding:40 }}>加载中...</div> :
       posts.length===0 ? <div style={{ textAlign:'center',color:'#8f98a0',padding:40 }}>还没有人发表情，来做第一个吧！</div> :
       (<>
       {posts.map(p=>(
         <div key={p.id} style={{ background:'rgba(30,34,50,0.95)',borderRadius:12,padding:16,marginBottom:12,border:'1px solid rgba(255,255,255,0.1)' }}>
           <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12 }}>
             <div className={`avatar-frame frame-${getAvatarFrameId()}`} style={{ width:40,height:40,flexShrink:0,...getAvatarFrameStyle() }}>
               <img src={user?.avatar || '/avatar.png'} alt="" className="user-avatar" draggable="false" style={{ width:'100%',height:'100%',filter:'blur(4px)',WebkitUserDrag:'none',MozUserDrag:'none' }} onError={(e) => { e.target.onerror = null; e.target.src = getDefaultAvatar(user?.username); }} onContextMenu={(e) => e.preventDefault()} />
             </div>
             <div><div style={{ color:'#8f98a0',fontSize:14 }}>匿名用户</div><div style={{ color:'#6b7280',fontSize:12 }}>{p.created_at}</div></div>
           </div>
           <div style={{ fontSize:48,textAlign:'center',padding:'20px 0',fontFamily:'Apple Color Emoji,Segoe UI Emoji,Noto Color Emoji,Android Emoji,EmojiSymbols' }}>{p.emoji}</div>
           <div style={{ display:'flex',gap:20,color:'#8f98a0',fontSize:13 }}>
             <span onClick={()=>toast.error('点赞功能开发中，敬请期待！')} style={{ cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}><FiHeart/> {p.likes||0}</span>
             <span onClick={()=>toast.error('评论功能开发中，敬请期待！')} style={{ cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}><FiMessageCircle/> {p.comments||0}</span>
             <span onClick={()=>toast.error('分享功能开发中，敬请期待！')} style={{ cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}><FiShare2/></span>
             <span onClick={()=>toast.error('举报功能开发中，敬请期待！')} style={{ cursor:'pointer',display:'flex',alignItems:'center',gap:4 }}><FiFlag/></span>
           </div>
         </div>
       ))}
       {hasMorePosts && (
         <div style={{ textAlign:'center',padding:16 }}>
           <button onClick={() => fetchPosts(false)} disabled={loadingMore} style={{ background:'rgba(255,255,255,0.05)',color:'#8f98a0',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,padding:'8px 24px',cursor: loadingMore ? 'not-allowed' : 'pointer',opacity: loadingMore ? 0.5 : 1 }}>
             {loadingMore ? '加载中...' : '加载更多'}
           </button>
         </div>
       )}
       </>)}
    </div>
    </>
  );
}

// 分享弹窗组件
function ShareDetectPopup({ postId, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 99999
    }}>
      <div style={{
        background: '#1b2838', border: '2px solid #00ff88',
        borderRadius: 12, padding: '32px', textAlign: 'center', maxWidth: 400
      }}>
        <h3 style={{ color: '#00ff88', marginBottom: 16 }}>检测到分享帖子</h3>
        <p style={{ color: '#c6d4df', marginBottom: 24 }}>是否查看帖子？</p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
          <button onClick={onConfirm} style={{
            background: '#00ff88', color: '#0b1622', border: 'none',
            borderRadius: 8, padding: '10px 28px', fontWeight: 'bold', cursor: 'pointer'
          }}>是</button>
          <button onClick={onCancel} style={{
            background: 'transparent', color: '#8f98a0', border: '1px solid #4a6b8a',
            borderRadius: 8, padding: '10px 28px', cursor: 'pointer'
          }}>否</button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const { user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isSharePage = location.pathname.startsWith('/shared');
  const isCodeEditor = location.pathname.startsWith('/maps/edit/') || location.pathname.startsWith('/maps/new');
  const isLandscapeEditor = isCodeEditor || location.pathname.startsWith('/interactive/edit/') || location.pathname.startsWith('/interactive/new');

  useEffect(() => {
    document.documentElement.classList.toggle('editor-landscape-required', isLandscapeEditor);
    return () => document.documentElement.classList.remove('editor-landscape-required');
  }, [isLandscapeEditor]);
  // 钱包页面是否隐藏导航栏（从编辑器跳转时隐藏，读 URL 参数）
  const isWalletRoute = location.pathname === '/wallet';
  const walletSearch = new URLSearchParams(location.search);
  const isWalletNavHidden = isWalletRoute && walletSearch.get('hideNav') === '1';
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // 账号资料以服务端为准：不能让另一台设备一直显示本地缓存的旧头像。
  // 实时事件只携带用户 ID，前端仍通过自己的登录凭证获取完整资料。
  useEffect(() => {
    if (!user?.id) return undefined;
    const refresh = () => {
      const state = useAuthStore.getState();
      if (state.token) state.refreshUser();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    refresh();
    const socket = io(window.location.origin, {
      query: { userId: user.id, username: user.username }
    });
    socket.on('profile-updated', (data) => {
      if (String(data?.userId) === String(user.id)) refresh();
    });
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    // WebSocket 断线时的兜底，页面可见期间每分钟核对一次。
    const fallbackTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !socket.connected) refresh();
    }, 60000);

    return () => {
      window.clearInterval(fallbackTimer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('profile-updated');
      socket.disconnect();
    };
  }, [user?.id]);

  // 全局路由滚动恢复：每次路由切换强制滚动到顶部
  // 注意：真正滚动容器是 .main-content（App.css 中 overflow-y:auto），不是 window
  useLayoutEffect(() => {
    const scrollToTop = () => {
      // 主滚动容器
      const main = document.querySelector(".main-content");
      if (main) main.scrollTop = 0;
      // 后备：window/document
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollToTop();
    const t1 = setTimeout(scrollToTop, 50);
    const t2 = setTimeout(scrollToTop, 200);
    const t3 = setTimeout(scrollToTop, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [location.pathname]);

  // 剪贴板分享检测
  const [sharePopup, setSharePopup] = useState(null); // { postId }
  const [shareChecked, setShareChecked] = useState(false);

  // 启动时检测剪贴板是否包含分享文本
  useEffect(() => {
    if (shareChecked) return;
    setShareChecked(true);
    const checkClipboard = async () => {
      try {
        if (!navigator.clipboard || !navigator.clipboard.readText) return;
        const text = await navigator.clipboard.readText();
        // 匹配分享文本格式：快来看看XXX的帖子，http://.../#/community/post/数字，复制这段文字...
        const match = text.match(/快来看看.*?的帖子.*?\/#\/community\/post\/(\d+)/);
        if (match) {
          setSharePopup({ postId: match[1] });
        }
      } catch (_) { /* 无权限读取剪贴板，静默忽略 */ }
    };
    // 延迟一点等页面稳定后再检测
    const timer = setTimeout(checkClipboard, 1500);
    return () => clearTimeout(timer);
  }, [shareChecked]);

  const handleShareConfirm = () => {
    setSharePopup(null);
    if (sharePopup?.postId) {
      navigate(`/community/post/${sharePopup.postId}`);
    }
  };

  const handleShareCancel = () => {
    setSharePopup(null);
  };

  // 屏幕缩放：从 localStorage 读取并应用，同时监听设置页的修改事件
  useEffect(() => {
    const applyZoom = (level) => {
      document.body.style.zoom = level;
    };
    const savedZoom = localStorage.getItem('codemap_zoom') || '1';
    applyZoom(savedZoom);

    const handler = (e) => applyZoom(e.detail);
    window.addEventListener('codemap-zoom-change', handler);
    return () => window.removeEventListener('codemap-zoom-change', handler);
  }, []);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  // 鼠标悬停或手指按下链接时预取目标页面代码块，点击时直接命中缓存。
  useEffect(() => {
    const onLinkIntent = (event) => {
      const target = event.target;
      const anchor = target && target.closest ? target.closest('a[href]') : null;
      if (anchor) prefetchByHref(anchor.getAttribute('href'));
    };
    document.addEventListener('mouseover', onLinkIntent, { passive: true });
    document.addEventListener('touchstart', onLinkIntent, { passive: true });
    return () => {
      document.removeEventListener('mouseover', onLinkIntent);
      document.removeEventListener('touchstart', onLinkIntent);
    };
  }, []);

  // 首屏稳定后，按顺序预取最常用的主导航页面。
  // 不一次性预取全部页面，避免重型编辑器和低概率页面挤占首屏带宽。
  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return undefined;

    const routes = user
      ? ['/store', '/community', '/maps', '/social']
      : ['/store', '/community', '/maps', '/login'];
    const timers = [];
    let idleId;
    let fallbackTimer;
    let cancelled = false;

    const schedule = () => {
      routes.forEach((route, index) => {
        timers.push(setTimeout(() => {
          if (!cancelled) prefetchRoute(route);
        }, 800 + index * 700));
      });
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(schedule, { timeout: 3000 });
    } else {
      fallbackTimer = setTimeout(schedule, 1800);
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (idleId !== undefined && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, [user]);

  return (
    <LanguageProvider>
      {sharePopup && (
        <ShareDetectPopup
          postId={sharePopup.postId}
          onConfirm={handleShareConfirm}
          onCancel={handleShareCancel}
        />
      )}
      <div className="app">
        {/* 提示气泡：从右上角改到顶部居中。
            原来放右上会撞两样东西——macOS 系统通知就在右上角（用户分不清是系统还是站内），
            也压着导航栏的头像菜单。顶部居中不挡操作区、手机键盘弹出也不会被顶走。 */}
        <Toaster position="top-center" limit={3} containerStyle={{ zIndex: 9999999, top: 76 }} toastOptions={{
          duration: 2600,
          style: {
            background: 'rgba(17, 24, 39, 0.94)',
            color: '#e8eaed',
            border: '1px solid rgba(79, 195, 247, 0.28)',
            borderRadius: 12,
            fontSize: 13.5,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.45)',
            maxWidth: '82vw',
          },
        }} />
        <WelcomeFlow />
        {!isSharePage && !isCodeEditor && !isWalletNavHidden && <Navbar />}
        {!isSharePage && !isCodeEditor && !isWalletNavHidden && <MobileTabBar />}
        {user?.isGuest && !isSharePage && !isCodeEditor && !isWalletNavHidden && (
          <div className="guest-mode-bar" role="status">
            <div className="guest-mode-inner">
              <span className="guest-mode-text">
                <b>试玩模式</b>
                <span>进度只留在这台设备</span>
              </span>
              <button type="button" onClick={() => navigate('/settings?tab=security')}>保存进度</button>
            </div>
          </div>
        )}
        <main className="main-content">
          <ErrorBoundary><Suspense fallback={<RouteFallback />}><Routes>
            <Route path="/" element={<Home />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/store" element={<Store />} />
            <Route path="/game/:id" element={<GameDetail />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/keyword/:keyword" element={<KeywordCommunityInline />} />
            <Route path="/community/post/:id" element={<PostDetail />} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/library" element={user ? <Library /> : <Navigate to="/login" />} />
            <Route path="/cart" element={user ? <Cart /> : <Navigate to="/login" />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/creator" element={user ? <Creator /> : <Navigate to="/login" />} />
            <Route path="/wallet" element={user ? <Wallet /> : <Navigate to="/login" />} />
            <Route path="/maps" element={<MapStore />} />
            <Route path="/my-works" element={user ? <MyWorks /> : <Navigate to="/login" />} />
            <Route path="/maps/:id" element={<GameDetail />} />
            <Route path="/maps/new" element={user ? <NewMap /> : <Navigate to="/login" />} />
            <Route path="/maps/edit/:id" element={user ? <NewMap /> : <Navigate to="/login" />} />
            <Route path="/maps/settings/:id" element={user ? <MapSettings /> : <Navigate to="/login" />} />
            <Route path="/interactive/new" element={<InteractiveEditor />} />
            <Route path="/interactive/edit/:id" element={<InteractiveEditor />} />
            <Route path="/code-market" element={<CodeMarket />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/msg-reports" element={<MessageReports />} />
            <Route path="/rules" element={<Rules />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/social" element={user ? <Social /> : <Navigate to="/login" />} />
            <Route path="/social/:userId" element={user ? <Social /> : <Navigate to="/login" />} />
            <Route path="/chat" element={<Navigate to="/social" />} />
            <Route path="/chat/:userId" element={<Navigate to="/social" />} />
            <Route path="/mails" element={user ? <Mails /> : <Navigate to="/login" />} />
            <Route path="/settings" element={user ? <Settings /> : <Navigate to="/login" />} />
            <Route path="/messages" element={user ? <Messages /> : <Navigate to="/login" />} />
            <Route path="/notifications" element={user ? <Notifications /> : <Navigate to="/login" />} />
            <Route path="/level-benefits" element={user ? <LevelBenefits /> : <Navigate to="/login" />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
            <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
            <Route path="/feedback" element={user ? <Feedback /> : <Navigate to="/login" />} />
            <Route path="/login-records" element={user ? <LoginRecords /> : <Navigate to="/login" />} />
            <Route path="/daily" element={user ? <Daily /> : <Navigate to="/login" />} />
            <Route path="/achievements" element={user ? <Achievements /> : <Navigate to="/login" />} />
            <Route path="/customer-service" element={user ? <CustomerService /> : <Navigate to="/login" />} />
            <Route path="/shared/:id" element={<ShareView />} />
            <Route path="/test-page" element={user ? <TestPage /> : <Navigate to="/login" />} />
          </Routes></Suspense></ErrorBoundary>
        </main>
        {!isSharePage && !isCodeEditor && !isWalletNavHidden && <Footer />}
      </div>
    </LanguageProvider>
  );
}

export default App;
